import React from "react";
import {
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  Chip,
  Box,
  IconButton,
  Tooltip,
  Avatar
} from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  ContentCopy as CopyIcon,
  OpenInNew as OpenIcon,
  Telegram as TelegramIcon,
  Twitter as TwitterIcon,
  Language as WebsiteIcon,
  Star as StarIcon
} from "@mui/icons-material";
import { Project } from "../state/atoms";

interface ProjectCardProps {
  project: Project;
  onEdit: (project: Project) => void;
  onDelete: (project: Project) => void;
  onCopyCode: (code: string) => void;
}

const ProjectCard: React.FC<ProjectCardProps> = ({
  project,
  onEdit,
  onDelete,
  onCopyCode
}) => {
  const handleCopyCode = () => {
    onCopyCode(project.unique_code);
  };

  const handleOpenWebsite = () => {
    if (project.website) {
      window.open(project.website, "_blank");
    }
  };

  const handleOpenTwitter = () => {
    if (project.twitter) {
      window.open(project.twitter, "_blank");
    }
  };

  const handleOpenTelegram = () => {
    if (project.telegram) {
      window.open(project.telegram, "_blank");
    }
  };

  return (
    <Card
      elevation={2}
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        borderRadius: 2,
        transition: "all 0.2s ease-in-out",
        "&:hover": {
          elevation: 4,
          transform: "translateY(-2px)",
          boxShadow: "0 8px 25px rgba(0, 0, 0, 0.15)"
        }
      }}
    >
      <CardContent sx={{ flexGrow: 1, p: { xs: 2, sm: 3 } }}>
        {/* Header with Avatar and Title */}
        <Box display="flex" alignItems="center" gap={2} mb={2}>
          <Avatar
            src={project.profile_logo_s3_url}
            sx={{
              width: { xs: 40, sm: 48 },
              height: { xs: 40, sm: 48 },
              background: "linear-gradient(135deg, #FFED00, #3D474E)",
              fontSize: { xs: "1rem", sm: "1.2rem" },
              fontWeight: "bold",
              flexShrink: 0
            }}
          >
            {!project.profile_logo_s3_url && project.name
              ? project.name.charAt(0).toUpperCase()
              : ""}
          </Avatar>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              variant="h6"
              component="h2"
              sx={{
                fontWeight: "bold",
                fontSize: { xs: "1rem", sm: "1.25rem" },
                wordBreak: "break-word",
                lineHeight: 1.2
              }}
            >
              {project.name}
            </Typography>
            {project.is_super_project && (
              <Chip
                icon={<StarIcon />}
                label="Super User"
                size="small"
                color="warning"
                variant="outlined"
                sx={{ mt: 0.5 }}
              />
            )}
          </Box>
        </Box>

        <Box display="flex" alignItems="center" gap={1} mb={2}>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
          >
            Code:
          </Typography>
          <Chip
            label={project.unique_code}
            size="small"
            variant="outlined"
            sx={{
              fontSize: { xs: "0.7rem", sm: "0.75rem" },
              height: 20,
              fontFamily: "monospace",
              background: "rgba(255, 237, 0, 0.1)",
              borderColor: "rgba(255, 237, 0, 0.3)",
              color: "#e8ecf1"
            }}
          />
        </Box>

        <Box display="flex" gap={1} mb={2} flexWrap="wrap">
          {project.features.chat && (
            <Chip label="Chat" size="small" color="success" />
          )}
          {project.features.ai_search && (
            <Chip label="AI Search" size="small" color="info" />
          )}
          {project.features.feedback && (
            <Chip label="Feedback" size="small" color="warning" />
          )}
        </Box>

        <Box display="flex" gap={1} mb={2} flexWrap="wrap">
          {project.website && (
            <Tooltip title="Visit website">
              <IconButton
                size="small"
                onClick={handleOpenWebsite}
                color="primary"
              >
                <WebsiteIcon />
              </IconButton>
            </Tooltip>
          )}
          {project.twitter && (
            <Tooltip title="Visit Twitter">
              <IconButton
                size="small"
                onClick={handleOpenTwitter}
                color="primary"
              >
                <TwitterIcon />
              </IconButton>
            </Tooltip>
          )}
          {project.telegram && (
            <Tooltip title="Visit Telegram">
              <IconButton
                size="small"
                onClick={handleOpenTelegram}
                color="primary"
              >
                <TelegramIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        {project.contract_address && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              mb: 1,
              wordBreak: "break-all",
              fontSize: { xs: "0.75rem", sm: "0.875rem" }
            }}
          >
            Contract: {project.contract_address.slice(0, 8)}...
            {project.contract_address.slice(-6)}
          </Typography>
        )}

        {project.chain && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              mb: 1,
              fontSize: { xs: "0.75rem", sm: "0.875rem" }
            }}
          >
            Chain: {project.chain}
          </Typography>
        )}

        {project.stats && (
          <Box
            mt={2}
            sx={{
              background: "rgba(0, 0, 0, 0.02)",
              border: "1px solid rgba(0, 0, 0, 0.1)",
              borderRadius: 1,
              p: { xs: 1, sm: 1.5 }
            }}
          >
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                fontSize: { xs: "11px", sm: "12px" },
                lineHeight: 1.4,
                wordBreak: "break-word"
              }}
            >
              Messages: {project.stats.total_messages} | Feedback:{" "}
              {project.stats.total_feedback}
              {project.stats.average_rating > 0 &&
                ` | Rating: ${project.stats.average_rating.toFixed(1)}`}
            </Typography>
          </Box>
        )}
      </CardContent>

      <CardActions sx={{ p: { xs: 1.5, sm: 2 }, pt: 0, gap: 1 }}>
        {/* Primary CTA - Edit Project */}
        <Button
          variant="outlined"
          size="small"
          startIcon={<EditIcon />}
          onClick={() => onEdit(project)}
          sx={{
            flex: 1,
            borderColor: "#FFED00",
            color: "#FFED00",
            fontSize: { xs: "0.75rem", sm: "0.875rem" },
            fontWeight: 600,
            "&:hover": {
              borderColor: "#FFF27A",
              background: "rgba(255, 237, 0, 0.1)",
              transform: "translateY(-1px)"
            }
          }}
        >
          Edit
        </Button>

        {/* Secondary CTA - Delete Project */}
        <Button
          variant="outlined"
          size="small"
          startIcon={<DeleteIcon />}
          onClick={() => onDelete(project)}
          sx={{
            flex: 1,
            borderColor: "#3D474E",
            color: "#3D474E",
            fontSize: { xs: "0.75rem", sm: "0.875rem" },
            "&:hover": {
              borderColor: "#FFED00",
              background: "rgba(255, 237, 0, 0.1)",
              transform: "translateY(-1px)"
            }
          }}
        >
          Delete
        </Button>
      </CardActions>
    </Card>
  );
};

export default ProjectCard;
